import { useCallback, useEffect, useState } from "react"
import { Help, WithID } from "../../types/db";
import { HelpService } from "../../types/services/help/HelpService";
import { db } from "../../services/firebase";
import { applyVariablesToQuery, QueryVariables } from "../../services/db";

const helpService = new HelpService(db);

/** 
 * Take care of fetching the specific Help info and keeping it up to date (sub/listener)
 * Works for both HelpTopic and HelpArticle doc
 */
export const useHelpInfo = <T extends Help>(docId: string) => {
    const [info, setInfo] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setInfo(null);
        setIsLoading(false);
        setError(null)
        if (!docId) return;

        setIsLoading(true)
        return helpService
            .getDocumentRef(docId)
            .onSnapshot(
                (snap) => {
                    const data = helpService.documentToEntity<T>(snap);
                    if (!!data)
                        setInfo(data)
                    setIsLoading(false)
                },
                (error) => {
                    setIsLoading(false)
                    setError(error.message)
                })

    }, [docId])

    const _update = useCallback((data: Partial<T>) => {
        return helpService.update(docId, data)
    }, [docId])

    const _delete = useCallback(() => {
        return helpService.delete(docId)
    }, [docId])

    return {
        info,
        isLoading,
        isError: !!error,
        error,
        update: _update,
        delete: _delete,
    }
}

/** 
 * Take care of fetching the list of Help documents and keeping it up to date (sub/listener)
 * Works for both HelpTopic and HelpArticle docs
 */
export const useHelpDocs = <T extends Help>(type: 'topic' | 'article') => {
    const [docs, setDocs] = useState<WithID<T>[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setDocs([]);
        setIsLoading(false);
        setError(null)

        setIsLoading(true)
        const query = applyVariablesToQuery(helpService.getCollection(),
            {
                where: [
                    { fieldPath: 'type', opStr: '==', value: type }
                ]
            } as QueryVariables
        )

        return query.onSnapshot(
            (querySnapshot) => {
                console.log('onSnapshot', querySnapshot)
                querySnapshot.docChanges().forEach(docChange => {
                    // console.log('changes', docChange)
                    const info = helpService.documentToEntity<T>(docChange.doc);
                    if (!info) return;//if the info doesn't exist don't go farther
                    switch (docChange.type) {
                        case 'added':
                            //add to the doc list
                            setDocs(curDocs => ([...curDocs, info]))
                            break;
                        case 'modified':
                            //modify the doc list
                            setDocs(curDocs => {
                                return curDocs.map(t => (t.id === info.id) ? info : t)
                            })
                            break;
                        case 'removed':
                            //delete from the doc list
                            setDocs(curDocs => {
                                return curDocs.filter(l => l.id !== info.id)
                            })
                            break;
                    }
                })
                setIsLoading(false);
            },
            (error) => {
                setIsLoading(false);
                setError(error.message)
            })
    }, [type]);

    const _create = useCallback((data: Omit<T, 'type' | 'breadcrumbs'>) => {
        return helpService.create({
            ...data,
            type
        })
    }, [type]);

    const _delete = useCallback((docId: string) => {
        return helpService.delete(docId)
    }, [])

    return {
        docs,
        isLoading,
        isError: !!error,
        error,
        create: _create,
        delete: _delete,
    }
}